import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Room } from "../../../../../../../../models/room";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  title?: string;
  roomToUpdate?: Room;
  hasError?: boolean;
  openModal: boolean;
  isLoading: boolean;
  closeModal: () => void;
  submitRoom: (room: Room) => void;
}

const RoomModal = ({
  title,
  openModal,
  roomToUpdate,
  hasError,
  isLoading,
  closeModal,
  submitRoom,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [roomName, setRoomName] = useState<string>("");
  const [roomCode, setRoomCode] = useState<string>("");
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    roomName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (roomName) =>
        roomName ? roomName?.length > 0 && roomName !== "" : false
      ),
    roomCode: yup.string().typeError(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setRoomName("");
    setRoomCode("");
  };

  const handleValidateSuccess = (room?: Room): boolean => {
    schema.validateSync(
      {
        roomName: room?.name,
        roomCode: room?.code,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (room?: Room) => {
    try {
      return handleValidateSuccess(room);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = { name: roomName, code: roomCode, status } as Room;

    if (isValid(object)) {
      submitRoom(object);
    }
  };

  useEffect(() => {
    if (roomToUpdate) {
      setRoomName(roomToUpdate.name);
      setRoomCode(roomToUpdate?.code || "");
      setStatus(roomToUpdate?.status || "");
    }
  }, [roomToUpdate]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = { name: roomName, code: roomCode, status } as Room;

      isValid(object);
    }
  }, [roomName, roomCode, status, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="roomModal"
      open={openModal}
      handleClose={closeModal}
      title={title || (roomToUpdate ? translate("editLocation") : translate("createLocation"))}
      dataCyTitle="roomModalTitle"
      showActionButtons
      actionButtonProps={{
        onCancel: () => {
          clearFields();
          closeModal();
        },
        dataCySave: "submitButton",
        dataCyCancel: "cancelButton",
        textSave: roomToUpdate ? translate("update") : translate("create"),
        loading: isLoading,
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("locationStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="roomCode"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!(getErrorByField("roomCode") && getErrorByField("roomCode") !== "")}
              errorMessage={getErrorByField("roomCode")}
              value={roomCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoomCode(e.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="roomName"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!(getErrorByField("roomName") && getErrorByField("roomName") !== "")}
              errorMessage={getErrorByField("roomName")}
              value={roomName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoomName(e.target.value)}
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default RoomModal;
